import React, { useEffect, useState } from "react";
import Main from "../components/redesign/Main/Main";
import PageLoader from "../components/PageLoader/PageLoader";
import usePrize from "../hooks/usePrize";
import {
  doublePrizeData,
  heroInfoData,
  singlePrizeData,
} from "../lib/static-data";
import useAgeVerification from "../hooks/useAgeVerification";
import CtaButtonLayout from "../components/redesign/CtaButtonLayout/CtaButtonLayout";
import Footer from "../components/redesign/Footer/Footer";
import Header from "../components/Header/Header";
import MetaTags from "../components/meta-tags";
import AgeVerificationPopup from "../components/common/AgeVerificationPopup";
import useWidget from "../hooks/useWidget";
import { getGeneralContent } from "../services/generalContent";
import { getBonusDraw } from "../services/bonusDraw";
import { getFlashDraw } from "../services/flashDraw";

const buildFallbackContent = (raffle) => {
  const galleryImages = (raffle && raffle.property && raffle.property.galleryImages) || [];
  const floorPlanImage =
    (raffle && raffle.property && raffle.property.floorPlanImage) || "";
  const title = (raffle && raffle.title) || "Win the Dream Home";
  const s3 = process.env.REACT_APP_AWS_S3_URL || "";

  return {
    dreamHome: {
      video: {
        title,
        videoDesktopUrl: `${s3}videos/competition-video-03-01-2024.mp4`,
        videoMobileUrl: `${s3}videos/mobile-competition-video-03-01-2024.mp4`,
        videoPreviewUrl: `${s3}videos/preview-competition-video-03-01-2024.jpg`,
        videoPreviewMobileUrl: `${s3}videos/preview-competition-video-03-01-2024.jpg`,
        badgePrice: null,
        badgeUrl: null,
      },
      gallery: galleryImages.map((image, index) => ({
        _id: `${index}-${image}`,
        image800Url: image,
        image1600Url: image,
        image3840Url: image,
      })),
      floorPlanUrl: floorPlanImage,
      floorPlanMobileUrl: floorPlanImage,
      info: heroInfoData.map((item, index) => ({
        _id: `${index}-${item.title}`,
        title: item.title,
        description: (item.content || []).map((entry, entryIndex) =>
          typeof entry === "string"
            ? { _id: `${index}-${entryIndex}`, text: entry }
            : { _id: `${index}-${entryIndex}`, ...entry }
        ),
      })),
      timeLine: null,
    },
  };
};

function HomePage(props) {
  const {
    raffle,
    prizeId,
    prizeLoading,
    raffles,
    isExpired,
    activeRaffles,
    subscriptionModels,
    isDoublePrize,
  } = usePrize();

  const [content, setContent] = useState({});
  const [bonusDraw, setBonusDraw] = useState(null);
  const [flashDraw, setFlashDraw] = useState(null);
  const landingData = isDoublePrize ? doublePrizeData : singlePrizeData;
  const { showAgePopup, requireAgeVerification, confirmAge, closeAgePopup } =
    useAgeVerification();

  const getPageContent = async () => {
    const pageContent = await getGeneralContent();
    if (pageContent && pageContent.dreamHome) {
      setContent(pageContent);
    }
  };

  const getActiveBonusDraw = async () => {
    const activeBonusDraw = await getBonusDraw();
    if (Array.isArray(activeBonusDraw) && activeBonusDraw.length) {
      setBonusDraw(activeBonusDraw[0]);
    }
  };

  const getActiveFlashDraw = async () => {
    const activeFlashDraw = await getFlashDraw();
    if (activeFlashDraw) {
      setFlashDraw(activeFlashDraw);
    }
  };

  useEffect(() => {
    getPageContent();
    getActiveBonusDraw();
    getActiveFlashDraw();
  }, []);

  const { widget: timelineWidget, isWidgetLoading: isTimelineWidgetLoading } =
    useWidget({ type: "timeline-logo-rotation-text" });
  const { widget: footerWidget, isWidgetLoading: isFooterWidgetLoading } =
    useWidget({ type: "footer-logo-rotation-text" });

  const onClickExternalLink = (endpoint) => {
    if (process.env.REACT_APP_BASE_URL) {
      window.open(process.env.REACT_APP_BASE_URL + endpoint, "_self");
    }
  };
  const onCTAClick = () =>
    requireAgeVerification(() => onClickExternalLink(`/ticket-selector`));
  const onTwo4One = () => onClickExternalLink(`/2-4-1`);
  const onLearnMoreClick = () => onClickExternalLink(`/how-it-works`);
  const onLearnMoreCharityClick = () => onClickExternalLink(`/charity-raffle`);
  const onEnterNowClick = () =>
    requireAgeVerification(() => onClickExternalLink(`/ticket-selector`));

  const isLoading =
    prizeLoading || isTimelineWidgetLoading || isFooterWidgetLoading;
  const resolvedContent =
    content && content.dreamHome ? content : buildFallbackContent(raffle);
  return (
    <>
      <MetaTags />
      {isLoading ? (
        <CtaButtonLayout isDark>
          <Header raffle={raffle} />
          <PageLoader loading />
          <Footer insideComponent={true} widget={footerWidget} />
        </CtaButtonLayout>
      ) : (
        <Main
          prize={null}
          prizeId={prizeId}
          raffle={raffle}
          raffles={raffles}
          subscriptionModels={subscriptionModels}
          activeRaffles={activeRaffles}
          isExpired={isExpired}
          data={landingData}
          onCTAClick={onCTAClick}
          onTwo4One={onTwo4One}
          onLearnMoreClick={onLearnMoreClick}
          onLearnMoreCharityClick={onLearnMoreCharityClick}
          onEnterNowClick={onEnterNowClick}
          widgets={{ timelineWidget, footerWidget }}
          content={resolvedContent}
          bonusDraw={bonusDraw}
          flashDraw={flashDraw}
        />
      )}
      {showAgePopup && (
        <AgeVerificationPopup onConfirm={confirmAge} onClose={closeAgePopup} />
      )}
    </>
  );
}

export default HomePage;
